import React, { useState, useEffect } from "react";
import { Container, Row, Col, Card, Button, Form, Alert } from "react-bootstrap";
import PermissionWrapper from "../components/PermissionWrapper";
import PackageService, { Package } from "../services/PackageService";
import { apiGet } from "../services/BaseService";

const PackageAssignmentPage: React.FC = () => {
  const [countries, setCountries] = useState<{countryCode: string, name: string}[]>([]);
  const [states, setStates] = useState<{stateCode: string, name: string}[]>([]);
  const [groups, setGroups] = useState<any[]>([]);
  const [packages, setPackages] = useState<Package[]>([]);
  const [selectedGroup, setSelectedGroup] = useState<any>(null);
  const [selectedPackage, setSelectedPackage] = useState<Package | null>(null);
  const [groupCredits, setGroupCredits] = useState<any>(null);
  const [customSchedules, setCustomSchedules] = useState(1);
  const [customTickets, setCustomTickets] = useState(50);
  const [calculatedPrice, setCalculatedPrice] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  
  const [filters, setFilters] = useState({
    country: '',
    state: ''
  });

  useEffect(() => {
    fetchCountries();
    fetchPackages();
  }, []);

  useEffect(() => {
    if (filters.country) {
      fetchStates(filters.country);
      fetchGroups();
    } else {
      setStates([]);
      setGroups([]);
    }
  }, [filters.country]);

  useEffect(() => {
    if (filters.country) {
      fetchGroups();
    }
  }, [filters.state]);

  useEffect(() => {
    if (selectedPackage) {
      calculateCustomPrice();
    }
  }, [customSchedules, customTickets, selectedPackage]);

  const fetchCountries = async () => {
    try {
      const data = await PackageService.getCountries();
      setCountries(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching countries:', error);
    }
  };

  const fetchStates = async (countryCode: string) => {
    try {
      const data = await PackageService.getStates(countryCode);
      setStates(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching states:', error);
    }
  };

  const fetchGroups = async () => {
    try {
      let url = '/groups';
      const params = new URLSearchParams();
      if (filters.country) params.append('country', filters.country);
      if (filters.state) params.append('state', filters.state);
      
      if (params.toString()) {
        url = `/groups/filter?${params.toString()}`;
      }
      
      const data = await apiGet(url);
      setGroups(Array.isArray(data.groups || data) ? (data.groups || data) : []);
    } catch (error) {
      console.error('Error fetching groups:', error);
    }
  };

  const fetchGroupCredits = async (groupId: string) => {
    try {
      const credits = await PackageService.getGroupCredits(groupId);
      setGroupCredits(credits);
    } catch (error) {
      console.error('Error fetching group credits:', error);
      setGroupCredits(null);
    }
  };

  const fetchPackages = async () => {
    try {
      const data = await PackageService.getPackages();
      setPackages(data.filter(pkg => !pkg.isRestricted));
    } catch (error) {
      console.error('Error fetching packages:', error);
    }
  };

  const calculateCustomPrice = async () => {
    if (!selectedPackage) return;
    
    try {
      const priceData = await PackageService.calculatePrice(customSchedules, customTickets);
      setCalculatedPrice(priceData.discount?.finalPrice || priceData.totalPrice);
    } catch (error) {
      console.error('Error calculating price:', error);
    }
  };

  const handleAssignPredefinedPackage = async (pkg: Package) => {
    if (!selectedGroup) {
      setError('Please select a group first');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      // Submit payment request for the predefined package
      await PackageService.submitPaymentRequest({
        packageId: pkg._id,
        groupId: selectedGroup._id,
        amount: pkg.totalPrice,
        paymentMethod: 'upi',
        status: 'approved' // Auto-approve for admin assignment
      });
      
      setSuccess(`Package "${pkg.packageName}" assigned successfully to ${selectedGroup.name}`);
      fetchGroupCredits(selectedGroup._id); // Refresh credits
    } catch (error) {
      setError('Failed to assign package');
    } finally {
      setLoading(false);
    }
  };

  const handleAssignPackage = async () => {
    if (!selectedGroup || !selectedPackage) {
      setError('Please select both a group and package');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      // Create custom package with slider values
      const customPackage = await PackageService.createCustomPackage(customSchedules, customTickets);
      
      // Submit payment request for the group
      await PackageService.submitPaymentRequest({
        packageId: customPackage._id,
        groupId: selectedGroup._id,
        amount: calculatedPrice,
        paymentMethod: 'upi',
        status: 'approved' // Auto-approve for admin assignment
      });
      
      setSuccess(`Package assigned successfully to ${selectedGroup.name}`);
      setSelectedGroup(null);
      setSelectedPackage(null);
    } catch (error) {
      setError('Failed to assign package');
    } finally {
      setLoading(false);
    }
  };

  return (
    <PermissionWrapper pageName="ManagePackagePage">
      <Container fluid>
        <Row className="mb-4">
          <Col>
            <h2>
              <i className="bi bi-box-arrow-right me-2"></i>
              Assign Package to Group
            </h2>
          </Col>
        </Row>

        {error && <Alert variant="danger">{error}</Alert>}
        {success && <Alert variant="success">{success}</Alert>}

        {/* Group Filtering */}
        <Row className="mb-4">
          <Col>
            <Card>
              <Card.Header>
                <h5 className="mb-0">Filter Groups</h5>
              </Card.Header>
              <Card.Body>
                <Row>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>Country</Form.Label>
                      <Form.Select 
                        value={filters.country} 
                        onChange={(e) => {
                          setFilters({country: e.target.value, state: ''});
                          setSelectedGroup(null);
                        }}
                      >
                        <option value="">Select Country</option>
                        {countries.map(country => (
                          <option key={country.countryCode} value={country.countryCode}>
                            {country.name}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>State</Form.Label>
                      <Form.Select 
                        value={filters.state} 
                        onChange={(e) => {
                          setFilters({...filters, state: e.target.value});
                          setSelectedGroup(null);
                        }}
                        disabled={!filters.country}
                      >
                        <option value="">All States</option>
                        {states.map(state => (
                          <option key={state.stateCode} value={state.stateCode}>
                            {state.name}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>Group</Form.Label>
                      <Form.Select
                        value={selectedGroup?._id || ''}
                        onChange={(e) => {
                          const group = groups.find(g => g._id === e.target.value);
                          setSelectedGroup(group || null);
                          if (group) {
                            fetchGroupCredits(group._id);
                          } else {
                            setGroupCredits(null);
                          }
                        }}
                        disabled={!filters.country}
                      >
                        <option value="">Select Group</option>
                        {groups.map(group => (
                          <option key={group._id} value={group._id}>
                            {group.name} - {group.groupCode}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Package Selection & Custom Settings */}
        {selectedGroup && (
          <Row className="mb-4">
            <Col md={6}>
              <Card>
                <Card.Header>
                  <h5 className="mb-0">Select Base Package</h5>
                </Card.Header>
                <Card.Body>
                  {packages.map(pkg => (
                    <Card 
                      key={pkg._id} 
                      className={`mb-2 ${selectedPackage?._id === pkg._id ? 'border-primary' : ''}`}
                    >
                      <Card.Body className="py-2">
                        <div className="d-flex justify-content-between align-items-center">
                          <div 
                            style={{ cursor: 'pointer', flex: 1 }}
                            onClick={() => setSelectedPackage(pkg)}
                          >
                            <strong>{pkg.packageName}</strong>
                            <div className="small text-muted">
                              {pkg.scheduleCount} schedules × {pkg.ticketCountPerSchedule} tickets
                            </div>
                          </div>
                          <div className="d-flex align-items-center gap-2">
                            <div className="text-primary">₹{pkg.totalPrice}</div>
                            <Button 
                              size="sm" 
                              variant="success"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleAssignPredefinedPackage(pkg);
                              }}
                              disabled={loading}
                            >
                              Assign
                            </Button>
                          </div>
                        </div>
                      </Card.Body>
                    </Card>
                  ))}
                </Card.Body>
              </Card>
            </Col>
            
            <Col md={6}>
              <Card>
                <Card.Header>
                  <h5 className="mb-0">Custom Settings</h5>
                </Card.Header>
                <Card.Body>
                  <Form.Group className="mb-3">
                    <Form.Label>Schedules: {customSchedules}</Form.Label>
                    <Form.Range
                      min={1}
                      max={50}
                      value={customSchedules}
                      onChange={(e) => setCustomSchedules(parseInt(e.target.value))}
                    />
                  </Form.Group>
                  
                  <Form.Group className="mb-3">
                    <Form.Label>Tickets per Schedule: {customTickets}</Form.Label>
                    <Form.Range
                      min={50}
                      max={1000}
                      step={10}
                      value={customTickets}
                      onChange={(e) => setCustomTickets(parseInt(e.target.value))}
                    />
                  </Form.Group>
                  
                  <div className="text-center">
                    <h4 className="text-success">Total: ₹{calculatedPrice}</h4>
                    <Button 
                      variant="primary" 
                      onClick={handleAssignPackage}
                      disabled={loading || !selectedPackage}
                    >
                      {loading ? 'Assigning...' : 'Assign Package'}
                    </Button>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        )}

        {/* Selected Group Info & Credits */}
        {selectedGroup && (
          <Row>
            <Col md={6}>
              <Card className="border-success">
                <Card.Header className="bg-success text-white">
                  <h5 className="mb-0">Selected Group</h5>
                </Card.Header>
                <Card.Body>
                  <h6>{selectedGroup.name}</h6>
                  <p className="mb-1"><strong>Code:</strong> {selectedGroup.groupCode}</p>
                  <p className="mb-1"><strong>Location:</strong> {selectedGroup.state}, {selectedGroup.country}</p>
                  {selectedGroup.email && <p className="mb-1"><strong>Email:</strong> {selectedGroup.email}</p>}
                  {selectedGroup.phone && <p className="mb-0"><strong>Phone:</strong> {selectedGroup.phone}</p>}
                </Card.Body>
              </Card>
            </Col>
            <Col md={6}>
              <Card className="border-info">
                <Card.Header className="bg-info text-white">
                  <h5 className="mb-0">Current Credits</h5>
                </Card.Header>
                <Card.Body>
                  {groupCredits ? (
                    groupCredits.hasActivePackage ? (
                      <>
                        <div className="mb-2">
                          <strong>Package:</strong> {groupCredits.packageName || 'Active Package'}
                        </div>
                        <Row>
                          <Col xs={6}>
                            <div className="text-center p-2 bg-light rounded">
                              <div className="h4 text-primary mb-1">{groupCredits.remainingCredits?.scheduleCredits || 0}</div>
                              <small className="text-muted">Schedules Left</small>
                            </div>
                          </Col>
                          <Col xs={6}>
                            <div className="text-center p-2 bg-light rounded">
                              <div className="h4 text-success mb-1">{groupCredits.remainingCredits?.ticketCredits || 0}</div>
                              <small className="text-muted">Tickets Left</small>
                            </div>
                          </Col>
                        </Row>
                        <div className="mt-2 small text-muted">
                          <div>Used: {groupCredits.usedCredits?.scheduleCredits || 0} schedules, {groupCredits.usedCredits?.ticketCredits || 0} tickets</div>
                        </div>
                      </>
                    ) : (
                      <div className="text-center text-muted">
                        <i className="bi bi-exclamation-circle display-4 mb-2"></i>
                        <p>No active package found</p>
                      </div>
                    )
                  ) : (
                    <div className="text-center">
                      <div className="spinner-border text-info" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                    </div>
                  )}
                </Card.Body>
              </Card>
            </Col>
          </Row>
        )}
      </Container>
    </PermissionWrapper>
  );
};

export default PackageAssignmentPage;